import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { Image, User, Role, UserModel, Signature } from 'src/app/classes';
import { PopUpData, PopUpComponent } from "../pop-up/pop-up.component";
import { BackEndService } from "../../backend-service";
import { Guid } from "../../globals";
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-manage-user',
  templateUrl: './manage-user.component.html',
  styleUrls: ['./manage-user.component.css']
})
export class ManageUserComponent implements OnInit {
  /**
   * the event emitter emits the pop up data
   */
  @Output() popUp = new EventEmitter<PopUpData>();
  /**
   * the components users
   */
  public users: User[] = [];
  /**
   * the components roles
   */
  public roles: Role[] = [];
  /**
   * the current user's id
   */
  public userPointer: string = null;
  /**
   * a set of the current user's signatures
   */
  public signatures: Signature[] = [];
  /**
   * are currently signatures loading
   */
  public signaturesLoading: boolean = false;
  /**
   * the current signature picture's index within the array
   */
  public signaturePictureIndex = 0;
  /**
   * the method returns the password placeholder
   */
  get pwPH(): string {
    if (this.currentUser.isNew) return "Passwort";
    return "Passwort ändern";
  }
  /**
   * the method returns the confirm password placeholder
   */
  get repeatPwPH(): string {
    if (this.currentUser.isNew) return "Passwort wiederholen";
    return "Passwort zum Ändern wiederholen";
  }
  /**
   * the method returns the current user
   */
  get currentUser(): User {
    var user = this.users.find(x => x != null && x.id == this.userPointer);
    if (typeof (user) == "undefined") {
      return null;
    }
    return user;
  }

  _currentUserLockedEnd: moment.Moment = moment();

  get currentUserLockedEnd(): string {
    if (this._currentUserLockedEnd) return this._currentUserLockedEnd.format("DD.MM.YYYY HH:mm:ss");
    return "Noch nicht gesperrt";
  };

  set currentUserLockedEnd(value: string) {
    if (moment(value).isValid()) {
      this.backEndService.post("api/account/lock/" + this.userPointer, value).subscribe((result) => {
        this._currentUserLockedEnd = moment(value);
        this.currentUser.lockedOutEnd = value;
      }, (error) => {
        console.log(error);
      });
    }
  }
  /**
   * the form's username control
   */
  get userName() {
    return this.form.get("userName");
  }
  /**
   * the form's passwords control
   */
  get passwords() {
    return this.form.get("passwords");
  }
  /**
   * the form's password control
   */
  get password() {
    return this.form.get("passwords.password");
  }
  /**
  * the form's confirm password control
  */
  get confirmPassword() {
    return this.form.get("passwords.confirmPassword");
  }
  /**
   * the form's email control
   */
  get email() {
    return this.form.get("email");
  }
  /**
   * the form's roles control
   */
  get roleControl() {
    return this.form.get("roles");
  }
  /**
   * the form's email control
   */
  get initials() {
    return this.form.get("initials");
  }
  /**
   * the user's image
   */
  private _profilePicture: Image;
  /**
   * the user's banner image
   */
  private _bannerPicture: Image;
  /**
   * the method returns the current user's profile picture
   */
  get profilePicture(): Image {
    if (this._profilePicture != null) {
      return this._profilePicture;
    }
    if (this.currentUser == null) return null;
    if (this.currentUser.profilePicture != null) {
      var i = new Image();
      i.data = this.currentUser.profilePicture;
      i.mediaType = this.currentUser.profilePictureMediaType;
      return i;
    }
    return null
  }
  /**
   * the method returns the current user's profile picture
   */
  get profilePictureData() {
    var image = this.profilePicture;
    if (image == null) return null;
    return "data:" + image.mediaType + ";base64," + image.data;
  }
  /**
   * the method returns the current user's signature picture with the current index
   */
  get currentSignaturePictureData() {
    var image = this.signatures[this.signaturePictureIndex];
    if (!image) return null;
    return "data:" + image.signaturemediatype + ";base64," + image.signaturedata;
  }
  /**
   * the method returns whether the current user's lock date is in future
   */
  currentUserIsLocked(): boolean {
    if (moment(this._currentUserLockedEnd).isValid()) {
      if (this._currentUserLockedEnd.unix() > moment().unix()) {
        return true;
      }
    }
    return false;
  }
  /**
   * the method returns the profile picture data of an image
   */
  getProfilePictureData(image) {
    return "data:" + image.mediaType + ";base64," + image.data;
  }
  /**
   * the method returns the profile picture data of an user
   */
  getProfilePictureDataForUser(user: User) {
    var image = new Image();
    image.mediaType = user.profilePictureMediaType;
    image.data = user.profilePicture;
    return this.getProfilePictureData(image);
  }
  /**
   * the method returns the current user's banner picture
   */
  get bannerPicture(): Image {
    if (this._bannerPicture != null) {
      return this._bannerPicture;
    }
    if (this.currentUser == null) return null;
    if (this.currentUser.bannerPicture != null) {
      var i = new Image();
      i.data = this.currentUser.bannerPicture;
      i.mediaType = this.currentUser.bannerPictureMediaType;
      return i;
    }
    return null
  }
  /**
   * the method returns the current user's banner picture
   */
  get bannerPictureData() {
    var image = this.bannerPicture;
    if (image == null) return null;
    return "data:" + image.mediaType + ";base64," + image.data;
  }
  /**
   * the current components form
   */
  form: FormGroup;
  /**
   * the constructor is called on component creation
   */
  constructor(
    private _formBuilder: FormBuilder,
    private backEndService: BackEndService,
    private router: Router,
    private _popUp: MatSnackBar
  ) {
    this.form = _formBuilder.group({
      "userName": ['', Validators.required],
      "initials": ['', Validators.maxLength(3)],
      "email": ['', Validators.email],
      "passwords": this._formBuilder.group({
        "password": ['', [Validators.required, Validators.minLength(8)]],
        "confirmPassword": ['', [Validators.required, Validators.minLength(8)]],
        "currentPassword": ['']
      }, { validator: this.matchValidator }),
      "roles": ['']
    });
  }
  /**
   * the method adds a new user
   */
  addUser() {
    var newUsers = this.users.filter(x => x.isNew);
    var u: User = null;
    if (newUsers.length == 0) {
      u = new User();
      u.id = Guid.newGuid();
      u.isNew = true;
      this.users.push(u);
    } else {
      u = newUsers[0];
    }
    this.setUser(u);
  }
  /**
   * the method checks whether the current user can be deleted
   */
  canDeleteUser(): boolean {
    return !this.currentUser.isNew;
  }
  /**
   * the method starts the create signature process
   */
  createSignature() {
    this.router.navigate(["/signature", { "user": this.userPointer }]);
  }
  /**
   * the method checks whether the current user is valid or not
   */
  userIsValid(): boolean {
    if (this.currentUser.isNew) {
      return this.userName.valid && this.passwords.valid && this.email.valid;
    } else {
      return this.userName.valid && this.email.valid;
    }
  }
  /**
   * the method deletes the current user
   */
  deleteUser() {
    var u = this.currentUser;
    var confirmed = confirm("Möchten Sie den Nutzer " + u.userName + " wirklich löschen?");
    if (confirmed) {
      this.backEndService.delete(User.url + "/" + this.userPointer).subscribe(
        (success) => {
          this.removeUser();
          this.userPointer = null;
          this._currentUserLockedEnd = null;
          console.log(success);
        },
        (error) => {
          console.log(error);
        }
      );
    }
  }
  /**
   * the method deletes the current signature
   */
  deleteCurrentSignature() {
    let signature = this.signatures[this.signaturePictureIndex];
    if (!signature) return;
    this.backEndService.delete("api/signature/" + signature.signatureid).subscribe(() => {
      this.signatures.splice(this.signaturePictureIndex, 1);
      this.signaturePictureIndex = 0;
    });
  }
  /**
   * the method checks whether a profile picture is setted
   */
  hasProfilePicture(): boolean {
    var image = this.profilePicture;
    if (image == null) return false;
    return true;
  }
  /**
   * the method checks whether a banner picture is setted
   */
  hasBannerPicture(): boolean {
    var image = this.bannerPicture;
    if (image == null) return false;
    return true;
  }
  /**
   * the method checks whether the given value is empty or not
   */
  isEmpty(value: string) {
    if (value == null || value == "") return true;
    return false;
  }
  /**
   * the method loads all users on component initalization
   */
  ngOnInit() {
    this.backEndService.get(User.url).subscribe(
      (users: User[]) => {
        for (let user of users) {
          user.isNew = false;
        }
        this.users = users;
        console.log(users);
      },
      (error) => {
        console.log(error);
      }
    );
    this.backEndService.get(Role.url).subscribe(
      (roles: Role[]) => {
        for (let role of roles) {
          role.isNew = false;
        }
        this.roles = roles;
      },
      (error) => {
        console.log(error);
      }
    );
  }
  /**
   * the method returns whether the current password is valid and matches the confirm password
   */
  passwordIsValid() {
    return true;
  }
  /**
   * the method checks whether the password's match
   */
  passwordMatch(): boolean {
    if (this.isEmpty(this.form.controls.passwords.value.password) || this.isEmpty(this.form.controls.passwords.value.confirmPassword)) return true;
    return this.form.controls.passwords.value.confirmPassword == this.form.controls.passwords.value.password;
  }
  /**
   * the method removes the banner picture
   */
  removeBannerPicture() {
    this._bannerPicture = null;
    this.currentUser.bannerPictureMediaType = null;
    this.currentUser.bannerPicture = null;
  }
  /**
   * the method removes the profile picture
   */
  removeProfilePicture() {
    this._profilePicture = null;
    this.currentUser.profilePictureMediaType = null;
    this.currentUser.profilePicture = null;
  }
  /**
   * the method removes an user
   */
  removeUser() {
    var i = this.users.indexOf(this.currentUser);
    this.users.splice(i, 1);
    var output = [];
    for (let element of this.users) if (element != null && typeof (element) != "undefined") output.push(element);
    this.users = output;
    this.form.reset();
  }
  /**
   * the method set's the pop up data
   */
  setPopUp(data: PopUpData) {
    this._popUp.openFromComponent(PopUpComponent, {
      duration: 2000,
      data: data
    });
  }
  /**
   * the method saves an user
   */
  saveUser() {
    if (this.currentUser.isNew) {
      var newUser = this.toUserRegistrationModel();
      this.backEndService.post(UserModel.url, newUser).subscribe(
        (success: User) => {
          this._bannerPicture = null;
          this._profilePicture = null;
          this.removeUser();
          this.users.push(success);
          this.setUser(success);
          this.setPopUp(new PopUpData({
            "message": "Der Nutzer " + success.userName + " wurde angelegt",
            "icon": "done",
            "iconClass": "success"
          }));
        },
        (error) => {
          console.log(error);
        }
      );
    } else {
      var newUser = this.toUserRegistrationModel();
      this.backEndService.put(User.url, newUser).subscribe(
        (success: User) => {
          this._bannerPicture = null;
          this._profilePicture = null;
          this.removeUser();
          this.users.push(success);
          this.setUser(success);
          this.setPopUp(new PopUpData({
            "message": "Der Nutzer " + success.userName + " wurde geändert",
            "icon": "done",
            "iconClass": "success"
          }));
        },
        (error) => {
          console.log(error);
        }
      );
    }
  }
  /**
   * the method sets the current user
   */
  setUser(user: User) {
    this.signatures = [];
    this.signaturePictureIndex = 0;
    this.userPointer = user.id;
    if (user.lockedOutEnd) this._currentUserLockedEnd = moment.utc(user.lockedOutEnd).local();
    else this._currentUserLockedEnd = null;
    this.form.controls.userName.setValue(user.userName);
    this.form.controls.email.setValue(user.email);
    this.form.controls.initials.setValue(user.initials);
    this.form.controls.roles.setValue(user.roleIds);
    this._bannerPicture = null;
    this._profilePicture = null;
    this.signaturesLoading = true;
    this.backEndService.get("api/signature/byuser/" + user.id).subscribe((result: Signature[]) => {
      this.signaturesLoading = false;
      this.signatures = result;
    }, () => {
      this.signaturesLoading = false;
    });
  }
  /**
   * the method creates a user model of the current form
   */
  toUserRegistrationModel() {
    var model = new UserModel();
    model.userName = this.userName.value;
    model.password = this.password.value;
    model.confirmPassword = this.confirmPassword.value;
    model.email = this.email.value;
    model.initials = this.initials.value;
    model.roleIds = [];
    model.id = this.userPointer;
    if (this.roleControl.value != null && Array.isArray(this.roleControl.value)) model.roleIds = this.roleControl.value;
    var pImage = this.profilePicture;
    if (pImage != null) {
      model.profilePictureMediaType = pImage.mediaType;
      model.profilePicture = pImage.data;
    } else {
      model.profilePictureMediaType = null;
      model.profilePicture = null;
    }
    var bImage = this.bannerPicture;
    if (bImage != null) {
      model.bannerPictureMediaType = bImage.mediaType;
      model.bannerPicture = bImage.data;
    } else {
      model.bannerPicture = null;
      model.bannerPictureMediaType = null;
    }
    return model;
  }
  /**
   * the method checks whether the given username is already reservated
   */
  userNameIsReservated(): boolean {
    if (this.userName.value == null || typeof (this.userName.value) == "undefined") return false;
    var users = this.users.filter(x => x.userName != null && x.id != this.userPointer && x.userName.toLocaleLowerCase() == this.userName.value.toLocaleLowerCase());
    if (users.length > 0) return true;
    return false;
  }
  /**
   * the method validates a form group that the password equals the confirmation
   */
  matchValidator(group: FormGroup) {
    if (group.controls.password.value == group.controls.confirmPassword.value) {
      return null;
    }
    return {
      mismatch: true
    };
  }
  /**
   * the method sets the profile picture
   */
  setProfilePicture(files) {
    var profilePicture: Image = new Image();
    var image = files[0];
    if (image == null || typeof (image) == "undefined") return;
    const reader = new FileReader();
    reader.onload = ((e) => {
      profilePicture.data = window.btoa(<string>(<any>e.target).result);
      this._profilePicture = profilePicture;
    });
    reader.readAsBinaryString(image);
    profilePicture.mediaType = image.type;
  }
  /**
   * the method sets the banner picture
   */
  setBannerPicture(files) {
    var bannerPicture: Image = new Image();
    var image = files[0];
    if (image == null || typeof (image) == "undefined") return;
    const reader = new FileReader();
    reader.onload = ((e) => {
      bannerPicture.data = window.btoa(<string>(<any>e.target).result);
      this._bannerPicture = bannerPicture;
    });
    reader.readAsBinaryString(image);
    bannerPicture.mediaType = image.type;
  }

  unlockUser() {
    this.backEndService.get("api/account/unlock/" + this.userPointer).subscribe(() => {
      this._currentUserLockedEnd = moment();
      this.currentUser.lockedOutEnd = moment().format("DD.MM.YYYY HH:mm:ss");
    }, (error) => {
      console.log(error);
    });
  }
}
